/**
 * VistoeraTASK-00577 — "is this Canvas project MINE?" (personal-canvas recovery).
 *
 * Four concepts that must never be conflated:
 *  - ownership (`created_by_user_id`, surfaced as `is_mine`),
 *  - access (`can_view` — the Workspace Owner can VIEW members' private projects),
 *  - edit (`can_edit` / `access_mode`),
 *  - "recently opened" (client hint) / "currently viewing" / return context.
 *
 * Only ownership qualifies a project as a personal V-logo recovery target.
 * `can_view` / `can_edit` alone never do. Pure — no API / React imports so
 * node tests run without Vite env.
 */

export interface CanvasProjectOwnershipFields {
  is_mine?: boolean | null
  created_by_user_id?: string | null
  access_mode?: string | null
  permissions?: { can_view?: boolean; can_edit?: boolean } | null
}

/** `getContentProject` resolves an axios-style `{ data }`; tests pass the bare DTO. */
export function unwrapProjectDetail(res: unknown): unknown {
  const r = res as { data?: unknown } | null | undefined
  return r && typeof r === 'object' && 'data' in r && r.data && typeof r.data === 'object' ? r.data : res
}

export function isOwnCanvasProject(project: unknown, userId: string | null | undefined): boolean {
  if (!userId || !project || typeof project !== 'object') return false
  const p = project as CanvasProjectOwnershipFields
  // A read-only view is never a personal canvas, whatever the creator field says.
  if (p.access_mode === 'READ_ONLY' || p.permissions?.can_edit === false) return false
  const creator = p.created_by_user_id
  if (typeof creator === 'string' && creator) return creator === userId
  // Explicitly unattributed legacy history: the server grants EDIT only to the
  // Workspace Owner for these rows (`project_permissions`), so editable == theirs.
  if (creator === null && p.permissions?.can_edit === true) return true
  return typeof p.is_mine === 'boolean' ? p.is_mine : false
}
